import { Fragment, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useBranding } from "@/app/branding";

import { MkCardGrid } from "./MkCardGrid";
import { MkFilterModal } from "./MkFilterModal";
import type { FilterGroup, FilterValue } from "./MkFilterModal";
import { MkPagination } from "./MkPagination";

const SEARCH_DEBOUNCE_MS = 300;
const NO_GROUP = "__mk_no_group__";

export interface TableColumn<T> {
    key: string;
    label: string;
    align?: "start" | "center" | "end";
    width?: string;
    sortable?: boolean;
    groupable?: boolean;
    icon?: string;
    cardTitle?: boolean;
    html?: boolean;
    tight?: boolean;
    aggregate?: "sum" | "avg" | "count" | ((rows: T[]) => unknown);
    aggregateFormat?(value: unknown, rows: T[]): string;
    editable?: boolean | "text" | "number" | "boolean";
    onCellClick?(row: T, event: ReactMouseEvent): void;
    render?(row: T): ReactNode;
    value?(row: T): unknown;
}

export interface RowAction<T> {
    icon: string;
    title: string;
    variant?: string;
    visible?(row: T): boolean;
    run(row: T): void;
}

export interface TableFilter {
    id: string;
    label: string;
    badge?: number;
}

export interface GroupOption<T> {
    id: string;
    label: string;
    icon?: string;
    key?: string;
    value?(row: T): unknown;
    format?(raw: unknown, row: T): string;
}

export interface BulkAction<T> {
    label: string;
    icon?: string;
    variant?: string;
    onClick(rows: T[]): void;
}

interface Props<T> {
    columns: TableColumn<T>[];
    rows: T[];
    rowKey(row: T, index: number): string | number;
    loading?: boolean;
    hover?: boolean;
    emptyIcon?: string;
    emptyText?: string;
    emptyActionLabel?: string;
    onEmptyAction?(): void;
    newLabel?: string;
    newIcon?: string;
    onNew?(): void;
    searchable?: boolean;
    searchPlaceholder?: string;
    query?: string;
    onQuery?(value: string): void;
    onSearch?(value: string): void;
    filterLabel?: string;
    filterCount?: number;
    onFilters?(): void;
    filters?: TableFilter[];
    activeFilter?: string;
    onFilterChange?(id: string, filter: TableFilter): void;
    filterGroups?: FilterGroup[];
    filterValue?: FilterValue;
    onFiltersApply?(value: FilterValue): void;
    groupOptions?: GroupOption<T>[];
    groupBy?: string | null;
    onGroupChange?(id: string | null, option: GroupOption<T> | null): void;
    groupCollapsed?: boolean;
    selectable?: boolean;
    onSelectionChange?(rows: T[]): void;
    bulkActions?: BulkAction<T>[];
    onCellSave?(row: T, column: TableColumn<T>, value: unknown): void | Promise<void>;
    defaultView?: "table" | "cards";
    viewToggle?: boolean;
    cardRender?(row: T): ReactNode;
    cardFields?: TableColumn<T>[];
    cardMaxLines?: number;
    accentOf?(row: T): string;
    actions?: RowAction<T>[];
    onRowClick?(row: T): void;
    rowClass?(row: T, index: number): string;
    page?: number;
    pageSize?: number;
    total?: number;
    onPage?(page: number): void;
    onSort?(order: string): void;
    toolbarExtra?: ReactNode;
    className?: string;
}

/** One declared width per column, so the header and the rows share a single
 *  authority and can never drift apart as the content changes. */
function columnWidth<T>(column: TableColumn<T>) {
    if (column.width) {
        return { width: column.width };
    }
    return column.align === "center" || column.align === "end"
        ? { width: "8rem" } : undefined;
}

function rawValue<T>(column: TableColumn<T>, row: T): unknown {
    const value = (row as Record<string, unknown>)[column.key];
    if (Array.isArray(value)) {
        return value[1] ?? value[0];
    }
    if (value && typeof value === "object" && "name" in (value as object)) {
        return (value as { name: unknown }).name;
    }
    return value;
}

export function cellValue<T>(column: TableColumn<T>, row: T): ReactNode {
    const value = column.render ? column.render(row) : (rawValue(column, row) as ReactNode);
    if (value === false || value === null || value === undefined) {
        return "";
    }
    if (typeof value === "boolean") {
        return String(value);
    }
    return value;
}

function plainValue<T>(column: TableColumn<T>, row: T): unknown {
    if (column.value) {
        return column.value(row);
    }
    const value = cellValue(column, row);
    if (typeof value === "string" || typeof value === "number") {
        return value;
    }
    const raw = rawValue(column, row);
    return raw === false || raw === null || raw === undefined ? "" : raw;
}

function compareValues(a: unknown, b: unknown): number {
    const aEmpty = a === null || a === undefined || a === "";
    const bEmpty = b === null || b === undefined || b === "";
    if (aEmpty && bEmpty) {
        return 0;
    }
    if (aEmpty) {
        return 1;
    }
    if (bEmpty) {
        return -1;
    }
    if (typeof a === "number" && typeof b === "number") {
        return a - b;
    }
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
        return na - nb;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
        return "";
    }
    return (Math.round(value * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

interface Group<T> {
    id: string;
    label: string;
    rows: T[];
    agg: Record<string, string>;
}

export function MkTable<T>({
    columns, rows, rowKey, loading, hover = true, emptyIcon, emptyText, emptyActionLabel = "",
    onEmptyAction, newLabel, newIcon, onNew, searchable, searchPlaceholder, query, onQuery,
    onSearch, filterLabel, filterCount, onFilters, filters, activeFilter, onFilterChange, filterGroups,
    filterValue, onFiltersApply, groupOptions, groupBy, onGroupChange, groupCollapsed = true,
    selectable = false, onSelectionChange, bulkActions, onCellSave, defaultView = "table",
    viewToggle = false, cardRender, cardFields = [], cardMaxLines, accentOf, actions, onRowClick,
    rowClass, page, pageSize, total, onPage, onSort, toolbarExtra, className,
}: Props<T>) {
    const { t } = useTranslation();
    const { pageSize: themePageSize } = useBranding();
    const [sort, setSort] = useState<{ key: string | null; dir: 1 | -1 }>({ key: null, dir: 1 });
    const [innerQuery, setInnerQuery] = useState("");
    const [innerPage, setInnerPage] = useState(1);
    const [selected, setSelected] = useState<Record<string, T>>({});
    const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
    const [view, setView] = useState<"table" | "cards">(defaultView === "cards" ? "cards" : "table");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [innerGroup, setInnerGroup] = useState<string | null>(null);
    const [groupsOpen, setGroupsOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});
    const filtersRef = useRef<HTMLDivElement>(null);
    const groupsRef = useRef<HTMLDivElement>(null);
    const searchTimer = useRef<number>();

    useEffect(() => {
        setView(defaultView === "cards" ? "cards" : "table");
    }, [defaultView]);

    useEffect(() => {
        setGroupOpen({});
    }, [groupBy]);

    useEffect(() => () => window.clearTimeout(searchTimer.current), []);

    useEffect(() => {
        function away(event: MouseEvent) {
            const target = event.target as Node;
            if (filtersRef.current && !filtersRef.current.contains(target)) {
                setFiltersOpen(false);
            }
            if (groupsRef.current && !groupsRef.current.contains(target)) {
                setGroupsOpen(false);
            }
        }
        window.addEventListener("click", away);
        return () => window.removeEventListener("click", away);
    }, []);

    const rowId = (row: T, index: number) => String(rowKey(row, index));

    useEffect(() => {
        const ids = new Set(rows.map((row, index) => rowId(row, index)));
        const stale = Object.keys(selected).filter((id) => !ids.has(id));
        if (stale.length) {
            const next = { ...selected };
            for (const id of stale) {
                delete next[id];
            }
            setSelected(next);
            onSelectionChange?.(rows.filter((row, index) => rowId(row, index) in next));
        }
    }, [rows]);

    const serverSide = Boolean(onPage);
    const currentQuery = onQuery ? (query ?? "") : innerQuery;

    const filtered = useMemo(() => {
        if (serverSide || onQuery) {
            return rows;
        }
        const term = innerQuery.trim().toLowerCase();
        if (!term) {
            return rows;
        }
        return rows.filter((row) => columns.some(
            (column) => String(plainValue(column, row) ?? "").toLowerCase().includes(term)));
    }, [rows, columns, innerQuery, serverSide, onQuery]);

    const sorted = useMemo(() => {
        if (serverSide || !sort.key) {
            return filtered;
        }
        const column = columns.find((item) => item.key === sort.key);
        if (!column) {
            return filtered;
        }
        return [...filtered].sort(
            (a, b) => compareValues(plainValue(column, a), plainValue(column, b)) * sort.dir);
    }, [filtered, sort, columns, serverSide]);

    const size = pageSize !== undefined ? pageSize : (themePageSize > 0 ? themePageSize : 15);
    const totalRows = serverSide ? (total || 0) : filtered.length;

    const groupChoices = useMemo<GroupOption<T>[]>(() => {
        if (groupOptions && groupOptions.length) {
            return groupOptions;
        }
        return columns.filter((column) => column.groupable)
            .map((column) => ({ id: column.key, key: column.key, label: column.label, icon: column.icon }));
    }, [groupOptions, columns]);

    const activeGroupId = groupBy === undefined ? innerGroup : groupBy;
    const activeGroup = activeGroupId ? groupChoices.find((choice) => choice.id === activeGroupId) ?? null : null;
    const grouped = Boolean(activeGroup);

    const groups = useMemo<Group<T>[]>(() => {
        if (!activeGroup) {
            return [];
        }
        const valueOf = (row: T) => (activeGroup.value
            ? activeGroup.value(row) : (row as Record<string, unknown>)[activeGroup.key ?? activeGroup.id]);
        const boolField = sorted.some((row) => valueOf(row) === true);
        const map = new Map<string, Group<T>>();
        for (const row of sorted) {
            const raw = valueOf(row);
            let entry: { id: string; label: string };
            if (raw === true) {
                entry = { id: "1", label: t("mk.table.yes") };
            } else if (raw === false && boolField) {
                entry = { id: "0", label: t("mk.table.no") };
            } else if (raw === false || raw === null || raw === undefined || raw === "") {
                entry = { id: NO_GROUP, label: t("mk.table.none") };
            } else if (Array.isArray(raw)) {
                entry = { id: String(raw[0]), label: String(raw[1] ?? raw[0]) };
            } else if (typeof raw === "object" && raw && "id" in raw) {
                const ref = raw as { id: number; name?: string };
                entry = { id: String(ref.id), label: String(ref.name ?? ref.id) };
            } else {
                const column = activeGroup.value ? null : columns.find((item) => item.key === activeGroup.key);
                let label = activeGroup.format ? activeGroup.format(raw, row)
                    : (column ? plainValue(column, row) : raw);
                if (label === "" || label === null || label === undefined) {
                    label = raw;
                }
                entry = { id: String(raw), label: String(label) };
            }
            let group = map.get(entry.id);
            if (!group) {
                group = { id: entry.id, label: entry.label, rows: [], agg: {} };
                map.set(entry.id, group);
            }
            group.rows.push(row);
        }
        const list = [...map.values()].sort((a, b) => {
            if (a.id === NO_GROUP) {
                return 1;
            }
            if (b.id === NO_GROUP) {
                return -1;
            }
            return compareValues(a.label, b.label);
        });
        for (const group of list) {
            for (const column of columns.filter((item) => item.aggregate)) {
                const kind = column.aggregate;
                let value: unknown;
                if (typeof kind === "function") {
                    value = kind(group.rows);
                } else if (kind === "count") {
                    value = group.rows.length;
                } else {
                    let sum = 0;
                    let count = 0;
                    for (const row of group.rows) {
                        const number = Number((row as Record<string, unknown>)[column.key]);
                        if (Number.isFinite(number)) {
                            sum += number;
                            count += 1;
                        }
                    }
                    value = kind === "avg" ? (count ? sum / count : 0) : sum;
                }
                group.agg[column.key] = column.aggregateFormat
                    ? column.aggregateFormat(value, group.rows)
                    : (typeof value === "number" ? formatNumber(value) : String(value ?? ""));
            }
        }
        return list;
    }, [activeGroup, sorted, columns, t]);

    const paginationTotal = grouped && !serverSide ? groups.length : totalRows;
    const pageCount = size ? Math.max(1, Math.ceil(paginationTotal / size)) : 1;
    const currentPage = serverSide ? (page || 1) : innerPage;
    const clampedPage = Math.min(currentPage, pageCount);

    const visibleRows = useMemo(() => {
        if (serverSide || !size || grouped) {
            return sorted;
        }
        const start = (clampedPage - 1) * size;
        return sorted.slice(start, start + size);
    }, [sorted, serverSide, size, grouped, clampedPage]);

    const visibleGroups = useMemo(() => {
        if (serverSide || !size) {
            return groups;
        }
        const start = (clampedPage - 1) * size;
        return groups.slice(start, start + size);
    }, [groups, serverSide, size, clampedPage]);

    const rangeText = (() => {
        if (!size || totalRows <= size) {
            return String(totalRows);
        }
        const start = (clampedPage - 1) * size + 1;
        const end = Math.min(totalRows, start + size - 1);
        return `${start}–${end} / ${totalRows}`;
    })();

    const selectedCount = Object.keys(selected).length;
    const hasActions = Boolean(actions?.length);
    const hasFilters = Boolean(filters?.length);
    const hasFilterModal = Boolean(filterGroups?.length);
    const filterModalCount = Object.values(filterValue ?? {}).reduce(
        (count, list) => count + (list ? list.length : 0), 0);
    const hasGroupBy = groupChoices.length > 0;
    const hasNew = Boolean(onNew && newLabel);
    const hasToolbar = Boolean(searchable || hasFilters || hasFilterModal || onFilters || hasGroupBy
                               || viewToggle || hasNew || toolbarExtra);
    const colCount = columns.length + (hasActions ? 1 : 0) + (selectable ? 1 : 0);
    const activeFilterObj = hasFilters ? filters!.find((filter) => filter.id === activeFilter) ?? null : null;
    const filterButtonLabel = !activeFilterObj || filters![0] === activeFilterObj ? "" : activeFilterObj.label;
    const skeletonRows = Array.from({ length: Math.min(size || 5, 10) }, (_, index) => index);

    function emit(next: Record<string, T>) {
        setSelected(next);
        onSelectionChange?.(rows.filter((row, index) => rowId(row, index) in next));
    }

    function isSelected(row: T, index: number) {
        return rowId(row, index) in selected;
    }

    function toggleRow(row: T, index: number, event: ReactMouseEvent) {
        event.stopPropagation();
        const id = rowId(row, index);
        const next = { ...selected };
        if (next[id]) {
            delete next[id];
        } else {
            next[id] = row;
        }
        emit(next);
    }

    const visibleAllSelected = visibleRows.length > 0
        && visibleRows.every((row, index) => isSelected(row, index));

    function toggleAllVisible(event: ReactMouseEvent) {
        event.stopPropagation();
        const next = { ...selected };
        visibleRows.forEach((row, index) => {
            const id = rowId(row, index);
            if (visibleAllSelected) {
                delete next[id];
            } else {
                next[id] = row;
            }
        });
        emit(next);
    }

    function isGroupSelected(group: Group<T>) {
        return group.rows.length > 0 && group.rows.every((row, index) => isSelected(row, index));
    }

    function toggleGroupSelection(group: Group<T>, event: ReactMouseEvent) {
        event.stopPropagation();
        const all = isGroupSelected(group);
        const next = { ...selected };
        group.rows.forEach((row, index) => {
            const id = rowId(row, index);
            if (all) {
                delete next[id];
            } else {
                next[id] = row;
            }
        });
        emit(next);
    }

    function isGroupOpen(id: string) {
        return groupOpen[id] === undefined ? !groupCollapsed : groupOpen[id];
    }

    function setGroup(choice: GroupOption<T> | null) {
        setGroupsOpen(false);
        setInnerGroup(choice ? choice.id : null);
        setGroupOpen({});
        setInnerPage(1);
        onGroupChange?.(choice ? choice.id : null, choice);
    }

    function setAllGroups(open: boolean) {
        setGroupsOpen(false);
        setGroupOpen(Object.fromEntries(groups.map((group) => [group.id, open])));
    }

    function setQuery(value: string) {
        setInnerPage(1);
        if (onQuery) {
            onQuery(value);
            return;
        }
        setInnerQuery(value);
        if (!serverSide) {
            return;
        }
        window.clearTimeout(searchTimer.current);
        if (!value.trim()) {
            onSearch?.("");
            return;
        }
        searchTimer.current = window.setTimeout(() => onSearch?.(value), SEARCH_DEBOUNCE_MS);
    }

    function goToPage(next: number) {
        setInnerPage(next);
        if (serverSide) {
            onPage?.(next);
        }
    }

    function toggleSort(column: TableColumn<T>) {
        if (!column.sortable) {
            return;
        }
        const dir: 1 | -1 = sort.key === column.key ? (sort.dir === 1 ? -1 : 1) : 1;
        setSort({ key: column.key, dir });
        setInnerPage(1);
        if (serverSide) {
            onSort?.(`${column.key} ${dir < 0 ? "desc" : "asc"}`);
        }
    }

    function editKey(column: TableColumn<T>, row: T, index: number) {
        const id = (row as Record<string, unknown>).id;
        return `${id !== undefined && id !== false ? id : index}::${column.key}`;
    }

    function onCell(column: TableColumn<T>, row: T, index: number, event: ReactMouseEvent) {
        if (column.editable && onCellSave) {
            event.stopPropagation();
            if (column.editable === "boolean") {
                void onCellSave(row, column, !(row as Record<string, unknown>)[column.key]);
                return;
            }
            const current = (row as Record<string, unknown>)[column.key];
            setEditing({ key: editKey(column, row, index),
                         value: current === false || current === undefined || current === null ? "" : String(current) });
            return;
        }
        if (column.onCellClick) {
            event.stopPropagation();
            column.onCellClick(row, event);
        }
    }

    async function commitEdit(column: TableColumn<T>, row: T) {
        const value = editing?.value ?? "";
        setEditing(null);
        await onCellSave?.(row, column, value);
    }

    function rowClasses(row: T, index: number, extra: string) {
        const classes: string[] = [];
        if (onRowClick) {
            classes.push("mk-row-click");
        }
        if (isSelected(row, index)) {
            classes.push("mk-row-selected");
        }
        const accent = accentOf ? accentOf(row) : "";
        if (accent) {
            classes.push(`mk-row-${accent}`);
        }
        const custom = rowClass?.(row, index);
        if (custom) {
            classes.push(custom);
        }
        return `${classes.join(" ")} ${extra}`;
    }

    function renderCell(column: TableColumn<T>, row: T, index: number) {
        const key = editKey(column, row, index);
        const cellClass = `${column.align ? `mk-text-${column.align} ` : ""}${
            column.onCellClick || (column.editable && onCellSave) ? "mk-cell-editable" : ""}`;
        if (editing && editing.key === key) {
            return (
                <td key={column.key} className={cellClass}>
                    <input className="mk-cell-input" type={column.editable === "text" ? "text" : "number"}
                           step="any" min="0" dir="ltr" autoFocus value={editing.value}
                           onChange={(event) => setEditing({ key, value: event.target.value })}
                           onBlur={() => void commitEdit(column, row)}
                           onKeyDown={(event) => {
                               if (event.key === "Enter") {
                                   event.preventDefault();
                                   void commitEdit(column, row);
                               } else if (event.key === "Escape") {
                                   event.preventDefault();
                                   setEditing(null);
                               }
                           }}
                           onClick={(event) => event.stopPropagation()} />
                </td>
            );
        }
        const value = cellValue(column, row);
        if (column.html || isValidElement(value) || (typeof value === "object" && value !== null)) {
            return (
                <td key={column.key} className={cellClass}
                    onClick={(event) => onCell(column, row, index, event)}>
                    {value}
                </td>
            );
        }
        const text = String(value);
        const tight = column.tight ?? !/\s/.test(text.trim());
        const raw = column.render ? column.render(row) : rawValue(column, row);
        return (
            <td key={column.key} className={cellClass}
                onClick={(event) => onCell(column, row, index, event)}>
                <span className={`mk-cell${text === "" ? " mk-cell-empty" : ""}${tight ? " mk-cell-tight" : ""}`}
                      title={typeof raw === "string" && raw ? raw : undefined}>
                    {text === "" ? "—" : text}
                </span>
            </td>
        );
    }

    function renderRow(row: T, index: number, extra: string) {
        return (
            <tr key={rowId(row, index)} className={rowClasses(row, index, extra)}
                onClick={() => onRowClick?.(row)}>
                {selectable && (
                    <td className="mk-table-select-cell">
                        <input type="checkbox" className="mk-table-checkbox"
                               checked={isSelected(row, index)} readOnly
                               onClick={(event) => toggleRow(row, index, event)} />
                    </td>
                )}
                {columns.map((column) => renderCell(column, row, index))}
                {hasActions && (
                    <td className="mk-table-actions-cell mk-text-end">
                        <div className="mk-table-actions">
                            {(actions ?? []).filter((action) => action.visible?.(row) !== false)
                                .map((action, actionIndex) => (
                                    <button key={actionIndex}
                                            className={`mk-table-action${action.variant ? ` mk-table-action-${action.variant}` : ""}`}
                                            title={action.title}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                action.run(row);
                                            }}>
                                        <i className={`fa ${action.icon}`} />
                                    </button>
                                ))}
                        </div>
                    </td>
                )}
            </tr>
        );
    }

    function renderCards(cardRows: T[]) {
        if (cardRender) {
            return (
                <div className="mk-cardgrid">
                    {loading ? Array.from({ length: Math.min(size || 8, 8) }, (_, index) => (
                        <div key={index} className="mk-cardgrid-card mk-cardgrid-skeleton">
                            <span className="mk-skeleton mk-skeleton-text" style={{ width: "60%" }} />
                            <span className="mk-skeleton mk-skeleton-text" />
                            <span className="mk-skeleton mk-skeleton-text" style={{ width: "80%" }} />
                        </div>
                    )) : cardRows.length ? cardRows.map((row, index) => (
                        <div key={rowId(row, index)} className="mk-cardgrid-slot">{cardRender(row)}</div>
                    )) : (
                        <div className="mk-cardgrid-empty">
                            <span className="mk-table-empty-icon"><i className={`fa ${emptyIcon || "fa-inbox"}`} /></span>
                            <span className="mk-table-empty-text">{emptyText}</span>
                            {emptyActionLabel && (
                                <button className="mk-table-empty-action" onClick={() => onEmptyAction?.()}>
                                    <i className="fa fa-plus" />
                                    <span>{emptyActionLabel}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            );
        }
        return (
            <MkCardGrid columns={columns} extraFields={cardFields} maxLines={cardMaxLines}
                        rows={cardRows} rowKey={rowKey} loading={loading} skeletonCount={size || 8}
                        onCardClick={onRowClick} accentOf={accentOf} emptyText={emptyText}
                        emptyIcon={emptyIcon} emptyActionLabel={emptyActionLabel}
                        onEmptyAction={onEmptyAction} />
        );
    }

    const footerVisible = (size && !loading && paginationTotal > size)
        || (selectable && selectedCount) || (grouped && !loading);

    return (
        <div className={`mk-table-wrap${className ? ` ${className}` : ""}`}>
            {hasToolbar && (
                <div className="mk-table-toolbar">
                    {hasNew && (
                        <button className="mk-table-new"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onNew?.();
                                }}>
                            <i className={`fa ${newIcon || "fa-plus"}`} />
                            <span>{newLabel}</span>
                        </button>
                    )}
                    {(hasFilterModal || onFilters) && (
                        <button className={`mk-table-filter ${(onFilters ? filterCount : filterModalCount) ? "mk-table-filter-active" : ""}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (onFilters) {
                                        onFilters();
                                    } else {
                                        setFilterModalOpen(true);
                                    }
                                }}>
                            <i className="fa fa-sliders" />
                            <span>{filterLabel || t("mk.table.filters")}</span>
                            {(onFilters ? filterCount : filterModalCount) ? (
                                <span className="mk-table-filter-badge">
                                    {onFilters ? filterCount : filterModalCount}
                                </span>
                            ) : null}
                        </button>
                    )}
                    {hasFilters && (
                        <div className="mk-table-filterwrap" ref={filtersRef}>
                            <button className={`mk-table-filter ${(filterButtonLabel ? "mk-table-filter-active " : "") + (filtersOpen ? "mk-table-filter-open" : "")}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setGroupsOpen(false);
                                        setFiltersOpen((open) => !open);
                                    }}>
                                <i className="fa fa-filter" />
                                <span>{t("mk.table.filters")}</span>
                                {filterButtonLabel && <span className="mk-table-filter-badge">{filterButtonLabel}</span>}
                                <i className="fa fa-angle-down" />
                            </button>
                            {filtersOpen && (
                                <div className="mk-table-filtermenu">
                                    {filters!.map((filter) => (
                                        <button key={filter.id}
                                                className={`mk-table-filteritem ${activeFilter === filter.id ? "mk-table-filteritem-active" : ""}`}
                                                onClick={() => {
                                                    setInnerPage(1);
                                                    setFiltersOpen(false);
                                                    onFilterChange?.(filter.id, filter);
                                                }}>
                                            <i className={`fa mk-table-filtercheck ${activeFilter === filter.id ? "fa-check-circle" : "fa-circle-o"}`} />
                                            <span>{filter.label}</span>
                                            {filter.badge !== undefined && (
                                                <span className="mk-table-filter-badge">{filter.badge}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {hasGroupBy && (
                        <div className="mk-table-filterwrap" ref={groupsRef}>
                            <button className={`mk-table-filter ${(grouped ? "mk-table-filter-active " : "") + (groupsOpen ? "mk-table-filter-open" : "")}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setFiltersOpen(false);
                                        setGroupsOpen((open) => !open);
                                    }}>
                                <i className="fa fa-object-group" />
                                <span>{t("mk.table.groupBy")}</span>
                                {grouped && <span className="mk-table-filter-badge">{activeGroup!.label}</span>}
                                <i className="fa fa-angle-down" />
                            </button>
                            {groupsOpen && (
                                <div className="mk-table-filtermenu">
                                    {groupChoices.map((choice) => (
                                        <button key={choice.id}
                                                className={`mk-table-filteritem ${activeGroupId === choice.id ? "mk-table-filteritem-active" : ""}`}
                                                onClick={() => setGroup(activeGroupId === choice.id ? null : choice)}>
                                            <i className={`fa mk-table-filtercheck ${activeGroupId === choice.id ? "fa-check-circle" : (choice.icon || "fa-circle-o")}`} />
                                            <span>{choice.label}</span>
                                        </button>
                                    ))}
                                    {grouped && (
                                        <>
                                            <div className="mk-table-menusep" />
                                            <button className="mk-table-filteritem" onClick={() => setAllGroups(true)}>
                                                <i className="fa mk-table-filtercheck fa-angle-double-down" />
                                                <span>{t("mk.table.expandAll")}</span>
                                            </button>
                                            <button className="mk-table-filteritem" onClick={() => setAllGroups(false)}>
                                                <i className="fa mk-table-filtercheck fa-angle-double-up" />
                                                <span>{t("mk.table.collapseAll")}</span>
                                            </button>
                                            <button className="mk-table-filteritem mk-table-filteritem-danger"
                                                    onClick={() => setGroup(null)}>
                                                <i className="fa mk-table-filtercheck fa-times" />
                                                <span>{t("mk.table.removeGrouping")}</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {toolbarExtra}
                    {searchable && (
                        <div className="mk-table-search">
                            <i className="fa fa-search" />
                            <input type="text" value={currentQuery}
                                   placeholder={searchPlaceholder || t("mk.table.search")}
                                   onChange={(event) => setQuery(event.target.value)} />
                            {currentQuery && (
                                <button className="mk-table-search-clear" onClick={() => setQuery("")}>
                                    <i className="fa fa-times" />
                                </button>
                            )}
                        </div>
                    )}
                    {viewToggle && (
                        <div className="mk-view-toggle">
                            <button className={`mk-view-toggle-btn ${view === "table" ? "mk-view-toggle-active" : ""}`}
                                    title={t("mk.table.list")}
                                    onClick={() => setView("table")}>
                                <i className="fa fa-list-ul" />
                            </button>
                            <button className={`mk-view-toggle-btn ${view === "cards" ? "mk-view-toggle-active" : ""}`}
                                    title={t("mk.table.cards")}
                                    onClick={() => {
                                        setView("cards");
                                        if (selectedCount) {
                                            emit({});
                                        }
                                    }}>
                                <i className="fa fa-th-large" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {view === "cards" && (grouped && !loading ? (
                <div className="mk-cardgroups">
                    {visibleGroups.map((group) => (
                        <div key={group.id} className="mk-cardgroup">
                            <button type="button"
                                    className={`mk-cardgroup-head ${isGroupOpen(group.id) ? "mk-cardgroup-open" : ""}`}
                                    onClick={() => setGroupOpen((current) => ({ ...current, [group.id]: !isGroupOpen(group.id) }))}>
                                <i className={`fa mk-table-groupcaret ${isGroupOpen(group.id) ? "fa-caret-down" : "fa-caret-right mk-table-groupcaret-closed"}`} />
                                <span className="mk-cardgroup-label">{group.label}</span>
                                <span className="mk-table-groupcount">{group.rows.length}</span>
                            </button>
                            {isGroupOpen(group.id) && renderCards(group.rows)}
                        </div>
                    ))}
                    {!visibleGroups.length && (
                        <div className="mk-cardgrid-empty">
                            <span className="mk-table-empty-icon"><i className={`fa ${emptyIcon || "fa-inbox"}`} /></span>
                            <span className="mk-table-empty-text">{emptyText || t("mk.table.noRecords")}</span>
                        </div>
                    )}
                </div>
            ) : renderCards(visibleRows))}

            {view === "table" && (
                <div className="mk-table-scroll">
                    <table className={`mk-table ${hover ? "mk-table-hover" : ""}`}>
                        <colgroup>
                            {selectable && <col className="mk-table-select-col" />}
                            {columns.map((column) => (
                                <col key={column.key} style={columnWidth(column)} />
                            ))}
                            {hasActions && <col className="mk-table-actions-col" />}
                        </colgroup>
                        <thead>
                            <tr>
                                {selectable && (
                                    <th className="mk-table-select-col">
                                        <input type="checkbox" className="mk-table-checkbox"
                                               checked={visibleAllSelected} readOnly
                                               onClick={toggleAllVisible} />
                                    </th>
                                )}
                                {columns.map((column) => (
                                    <th key={column.key}
                                        className={(column.align ? `mk-text-${column.align} ` : "")
                                                   + (column.sortable ? "mk-th-sortable" : "")}
                                        onClick={() => toggleSort(column)}>
                                        <span>{column.label}</span>
                                        {column.sortable && (
                                            <i className={`fa mk-th-sort ${sort.key === column.key
                                                ? (sort.dir === 1 ? "fa-sort-up mk-active" : "fa-sort-down mk-active")
                                                : "fa-sort"}`} />
                                        )}
                                    </th>
                                ))}
                                {hasActions && <th className="mk-table-actions-col mk-text-end" />}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? skeletonRows.map((index) => (
                                <tr key={index} className="mk-table-skeleton-row">
                                    {selectable && <td><span className="mk-skeleton mk-skeleton-text" /></td>}
                                    {columns.map((column) => (
                                        <td key={column.key}><span className="mk-skeleton mk-skeleton-text" /></td>
                                    ))}
                                    {hasActions && <td><span className="mk-skeleton mk-skeleton-text" /></td>}
                                </tr>
                            )) : !visibleRows.length ? (
                                <tr className="mk-table-empty-row">
                                    <td colSpan={colCount}>
                                        <div className="mk-table-empty">
                                            <span className="mk-table-empty-icon">
                                                <i className={`fa ${emptyIcon || "fa-inbox"}`} />
                                            </span>
                                            <span className="mk-table-empty-text">{emptyText || t("mk.table.noRecords")}</span>
                                            {emptyActionLabel && (
                                                <button className="mk-table-empty-action"
                                                        onClick={() => onEmptyAction?.()}>
                                                    <i className="fa fa-plus" />
                                                    <span>{emptyActionLabel}</span>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : grouped ? visibleGroups.map((group) => (
                                <Fragment key={group.id}>
                                    <tr className={`mk-table-grouprow ${isGroupOpen(group.id) ? "mk-table-grouprow-open" : ""}`}
                                        onClick={() => setGroupOpen((current) => ({ ...current, [group.id]: !isGroupOpen(group.id) }))}>
                                        {selectable && (
                                            <td className="mk-table-select-cell">
                                                <input type="checkbox" className="mk-table-checkbox"
                                                       checked={isGroupSelected(group)} readOnly
                                                       onClick={(event) => toggleGroupSelection(group, event)} />
                                            </td>
                                        )}
                                        <td className="mk-table-groupcell">
                                            <span className="mk-table-groupcellin">
                                                <i className={`fa mk-table-groupcaret ${isGroupOpen(group.id) ? "fa-caret-down" : "fa-caret-right mk-table-groupcaret-closed"}`} />
                                                <span className="mk-table-grouplabel">{group.label}</span>
                                                <span className="mk-table-groupcount">{group.rows.length}</span>
                                            </span>
                                        </td>
                                        {columns.slice(1).map((column) => (
                                            <td key={column.key}
                                                className={`mk-table-groupagg${column.align ? ` mk-text-${column.align}` : ""}`}>
                                                {group.agg[column.key] || ""}
                                            </td>
                                        ))}
                                        {hasActions && <td className="mk-table-groupagg" />}
                                    </tr>
                                    {isGroupOpen(group.id) && group.rows.map((row, index) => (
                                        renderRow(row, index, "mk-row-grouped")
                                    ))}
                                </Fragment>
                            )) : visibleRows.map((row, index) => renderRow(row, index, ""))}
                        </tbody>
                    </table>
                </div>
            )}

            {Boolean(footerVisible) && (
                <div className="mk-table-footer">
                    {selectable && selectedCount ? (
                        <div className="mk-table-bulk">
                            <span className="mk-table-bulkcount">
                                {selectedCount} {t("mk.table.selected")}
                            </span>
                            <button className="mk-table-clear-selection" title={t("mk.table.clearSelection")}
                                    onClick={() => emit({})}>
                                <i className="fa fa-times" />
                            </button>
                            {(bulkActions ?? []).map((action, index) => (
                                <button key={index}
                                        className={`mk-table-bulkbtn ${action.variant ? `mk-table-bulkbtn-${action.variant}` : ""}`}
                                        onClick={() => action.onClick(Object.values(selected))}>
                                    {action.icon && <i className={`fa ${action.icon}`} />}
                                    <span>{action.label}</span>
                                </button>
                            ))}
                        </div>
                    ) : grouped ? (
                        <span className="mk-table-count" dir="auto">
                            <i className="fa fa-object-group" />
                            {t("mk.table.groupSummary", { groups: groups.length, records: totalRows })}
                        </span>
                    ) : (
                        <span className="mk-table-count" dir="ltr">{rangeText}</span>
                    )}
                    {Boolean(size && paginationTotal > size) && (
                        <MkPagination page={clampedPage} total={paginationTotal} pageSize={size}
                                      onChange={goToPage} />
                    )}
                </div>
            )}
            {hasFilterModal && (
                <MkFilterModal open={filterModalOpen} groups={filterGroups!} value={filterValue ?? {}}
                               onApply={(value) => {
                                   setFilterModalOpen(false);
                                   setInnerPage(1);
                                   onFiltersApply?.(value);
                               }}
                               onClear={() => {
                                   setInnerPage(1);
                                   onFiltersApply?.({});
                               }}
                               onClose={() => setFilterModalOpen(false)} />
            )}
        </div>
    );
}
